/*
 * The one sign-out. Every page that offers "Log out" calls this.
 *
 * Before, App cleared the token and the pet cache but not the in-memory
 * pet store or the saved booking selection, and the appointments, packages
 * and perks pages each cleared only the token and the user — so after a
 * sign-out the pet picker still listed the last person's pets, which on a
 * shared phone shows one client's pets to the next.
 */
import { clearCMSAuth, clearPublicPets } from './auth.service';
import { bookingStateService } from './bookingState.service';
import { petsStore } from './pets.store';
import { clearPetPhotos } from './petPhoto.service';
import { storageService } from './storage.service';
import { PROMO_CLAIM_AFTER_SIGN_IN_KEY, PROMO_DISMISSED_KEY } from './promotion.service';
import { CAMPAIGN_LINK_KEY } from './campaignLink';
import { forgetLinkStore } from './campaignStore';
import { forgetMessages } from './messages.service';
import { forgetVisits } from './lastVisits.service';
import { PET_COPY_DISMISSED_KEY, forgetPetOffers } from './petCopy.service';
import { packageService } from './package.service';
import { whereStore } from './where.store';
import { forgetAddresses } from './mobile.service';

/** Keys that belong to the signed-in person and mean nothing to the next one. */
const PERSONAL_KEYS = [
  'user',
  'create_from_public_pets',
  'pre_login_step',
  'auto_select_pet',
  // The old Rebook's (retired 15 Oct 2026, G32): nothing writes them now, but
  // a phone that used it still holds a client's pets and services under them.
  'rebookData',
  'rebookMode',
  // Their perks wallet (2 min cache): the next person on a shared phone
  // briefly saw it (found 2 Oct 2026).
  'perks_wallet_cache_v1',
  // Promotions: offers they put off, and one waiting for them to sign in.
  PROMO_DISMISSED_KEY,
  PROMO_CLAIM_AFTER_SIGN_IN_KEY,
  // "Add your pets?" put off with Not now (G35).
  PET_COPY_DISMISSED_KEY,
  // The campaign link they came by (D21): theirs, and for their booking only.
  CAMPAIGN_LINK_KEY
  // Not here on purpose: `mpg_last_sign_in_method` (lastSignInMethod.ts), the
  // "Last used" badge. G1's one exception: the method word only, no contact or
  // name (Lena, 19 Oct 2026: "Method only, survives sign-out").
];

export const signOut = (): void => {
  try {
    clearCMSAuth();
    clearPublicPets();
    bookingStateService.clearBookingState();
    PERSONAL_KEYS.forEach(k => storageService.removeItem(k));
  } catch (err) {
    console.error('Clearing session data on sign-out failed:', err);
  }
  petsStore.reset();
  // The location a campaign's link named, if not yet applied (D27, this tab only).
  forgetLinkStore();
  clearPetPhotos();
  // Their Messages choices and email, held in memory (G27).
  forgetMessages();
  // Their past visits and their packs as last read, held in memory for the services step.
  forgetVisits();
  packageService.forgetMyPackages();
  // The pets offered from another salon, as last asked (G35).
  forgetPetOffers();
  // Where their booking was to happen: their address and its quote (mobile grooming, G1).
  whereStore.forget();
  forgetAddresses();
  // Let Google One Tap offer itself again on the next visit.
  try {
    (window as any).google?.accounts?.id?.disableAutoSelect();
  } catch {
    /* not loaded */
  }
};
